import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loginWithEmail = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/");
    } catch (err) {
      console.error(err.code);
      if (err.code === "auth/invalid-email") {
        setError("El correo electrónico no es válido.");
      } else if (err.code === "auth/user-not-found") {
        setError("No existe una cuenta con ese correo.");
      } else if (err.code === "auth/wrong-password") {
        setError("La contraseña es incorrecta.");
      } else {
        setError("Credenciales incorrectas.");
      }
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      navigate("/");
    } catch (err) {
      console.error(err.code);
      setError("Error al intentar iniciar sesión con Google.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#fffceb] to-white px-4">
      <div className="w-full max-w-md bg-white/60 backdrop-blur-lg shadow-xl rounded-2xl p-8 border border-yellow-100 animate-fade-in">
        <div className="flex justify-center mb-6">
          <img
            src="/noltrade_gpt.png"
            alt="Noltrade Logo"
            className="h-14 drop-shadow-md"
          />
        </div>

        {loading ? (
          <div className="flex justify-center p-6">
            <div className="w-12 h-12 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-center text-gray-800 mb-6">
              Inicia sesión en tu cuenta
            </h1>

            <input
              type="email"
              placeholder="Correo electrónico"
              className="w-full p-3 mb-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
            />

            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Contraseña"
                className="w-full p-3 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                onChange={(e) => setPassword(e.target.value)}
                value={password}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <div className="text-right text-sm mb-4">
              <Link to="/reset" className="text-yellow-600 hover:underline">
                ¿Has olvidado tu contraseña?
              </Link>
            </div>

            <button
              onClick={loginWithEmail}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold p-3 rounded-lg transition"
            >
              Entrar
            </button>

            <div className="my-4 text-center text-sm text-gray-500">o</div>

            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 p-3 rounded-lg hover:shadow-md transition"
            >
              <img src="/google-icon.svg" alt="Google" className="w-5 h-5" />
              Entrar con Google
            </button>

            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded text-center text-sm mt-4">
                {error}
              </div>
            )}

            <p className="text-center text-sm text-gray-600 mt-6">
              ¿No tienes cuenta?{" "}
              <Link to="/register" className="text-yellow-600 font-medium hover:underline">
                Regístrate
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
